import { CourseDetails } from '@/types/course.types';
import { AlertTriangle, Award, BarChart, Check, ChevronDown, ChevronLeft, Clock, Eye, Globe, Loader2, Lock, Settings, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import PublishConfirmModal from '@/components/ui/PublishConfirmModal';
import { getLanguageName, stripFlagEmoji } from '@/constants/languageOptions';
import { CourseTranslationSummary } from '@/services/course-translation.service';

// Course Stage type
type CourseStage = 'generating' | 'draft' | 'published' | 'unpublished';

interface CourseHeaderProps {
  courseDetails: CourseDetails;
  onNavigateBack: () => void;
  onOpenMetadata: () => void;
  onPreview: () => void;
  /** Awaited on publish so the confirm dialog can show progress until it settles. */
  onStageChange?: (stage: CourseStage) => void | Promise<void>;
  // SCORM courses are imported and not editable — hide metadata/stage editing.
  readOnly?: boolean;
  /**
   * All language versions of this course family (master + translations). Drives
   * the language switcher — a NAVIGATION control. Creating and managing
   * translations lives in the Metadata modal's Translations tab instead.
   */
  translations?: CourseTranslationSummary[];
  /** BCP-47 code of the master course, e.g. 'en-US'. */
  masterLanguageCode?: string;
  /** Master course id — the switcher's first entry navigates here. */
  masterCourseId?: string;
  /** Navigate to another language version of this course. */
  /**
   * Called with the target LANGUAGE CODE and whether it is the master. Never a
   * course id: every language of a course shares the master id, so only the
   * language changes.
   */
  onSwitchLanguage?: (languageCode: string, isMaster: boolean) => void;
  /** Open the Metadata modal on its Translations tab. */
  onManageTranslations?: () => void;
  /** Navigate to the master course this translation was created from. */
  onOpenSourceCourse?: () => void;
}

export default function CourseHeader({ courseDetails, onNavigateBack, onOpenMetadata, onPreview, onStageChange, readOnly = false, translations, masterLanguageCode, masterCourseId, onSwitchLanguage, onManageTranslations, onOpenSourceCourse }: CourseHeaderProps) {
  const [isStageDropdownOpen, setIsStageDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);


  // Ensure we have a valid stage, default to 'draft' if not present
  const currentStage = (courseDetails.stage || 'draft') as CourseStage;

  // Check if course is still generating
  const isGenerating = currentStage === 'generating';

  // A course created by translating another course.
  const isTranslatedCopy = !!courseDetails.source_course_id;

  // ── Language switcher ────────────────────────────────────────────────────
  // One entry per language in the family: the master first, then each
  // translation. Only versions that are finished are navigable — a language
  // still translating has no readable course behind it yet.
  const currentLanguageCode = courseDetails.language_code || masterLanguageCode || 'en-US';
  const labelFor = (code: string) =>
    getLanguageName(code) || code;

  const languageEntries = [
    ...(masterCourseId
      ? [{
          code: masterLanguageCode || 'en-US',
          isMaster: true,
          ready: true,
          stale: false,
          // The original is never stale and never re-translated.
          updating: false,
        }]
      : []),
    // translated_course_id is deliberately NOT used for navigation. It is the
    // id of a TranslatedPedagogicalCourse row, which the course endpoints do
    // not serve -- navigating to it returned 404.
    ...(translations ?? []).map((t) => {
      const status = t.status?.toUpperCase();
      const ready = Boolean(
        t.translated_course_id ||
        status === 'COMPLETED' ||
        status === 'PARTIALLY_FAILED'
      );
      return {
        code: t.language_code,
        isMaster: false,
        ready,
        stale: t.is_stale,
        // A language that HAS a previous version and is being re-run. It stays
        // selectable — the old version is a full one, just an edit behind.
        updating: ready && (status === 'IN_PROGRESS' || status === 'PENDING'),
      };
    }),
  ];

  // Shown once there is somewhere to GO: either another language exists, or
  // this course can have one added.
  const hasLanguageSwitcher =
    !!onSwitchLanguage && (languageEntries.length > 1 || !!onManageTranslations);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageDropdownOpen]);

  // Publishing is one-way: a course is a draft until it goes live, and after that
  // it is locked. Only these two are selectable.
  const stageOptions = [
    { value: 'draft', label: 'Draft', color: 'bg-yellow-500', description: 'Course is being developed' },
    { value: 'published', label: 'Published', color: 'bg-green-500', description: 'Available to learners' }
  ] as const;

  // Display-only labels. 'unpublished' is no longer selectable but still exists on
  // courses created before publishing became one-way, so it needs a correct label
  // rather than falling through to 'Draft'.
  const stageDisplay: Record<CourseStage, { label: string; color: string }> = {
    generating: { label: 'Generating', color: 'bg-blue-500' },
    draft: { label: 'Draft', color: 'bg-yellow-500' },
    published: { label: 'Published', color: 'bg-green-500' },
    unpublished: { label: 'Unpublished', color: 'bg-gray-500' }
  };

  // Published is terminal — no dropdown, no way back to draft.
  const isPublished = currentStage === 'published';

  const handleStageChange = (stage: CourseStage) => {
    setIsStageDropdownOpen(false);
    // Publishing can't be undone, so it gets a confirmation step.
    if (stage === 'published') {
      setIsPublishConfirmOpen(true);
      return;
    }
    onStageChange?.(stage);
  };

  // Kept open while the request runs: publishing validates every module server-side,
  // so it can take a moment and can still fail with something the admin must act on.
  const confirmPublish = async () => {
    setIsPublishing(true);
    try {
      await onStageChange?.('published');
    } finally {
      setIsPublishing(false);
      setIsPublishConfirmOpen(false);
    }
  };

  const currentStageOption = stageDisplay[currentStage] || stageDisplay.draft;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsStageDropdownOpen(false);
      }
    };

    if (isStageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStageDropdownOpen]);
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-visible">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-6 py-4 rounded-t-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Left: Back Button + Title */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={onNavigateBack}
              className="flex-shrink-0 p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Back to courses"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-white">{courseDetails.title}</h1>
            </div>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Published courses show a locked badge instead of the stage dropdown:
                publishing is final, so there is nothing left to switch to. */}
            {isPublished && (
              <div
                className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-lg border border-white/20 shadow-sm"
                title="This course is published and can no longer be edited or moved back to draft."
              >
                <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm"></div>
                <span className="text-sm font-semibold">Published</span>
                <Lock className="w-3.5 h-3.5 opacity-80" />
              </div>
            )}

            {/* Stage Dropdown */}
            {onStageChange && !readOnly && !isPublished && (
              <div className={`relative ${isStageDropdownOpen ? 'z-50' : ''}`} ref={dropdownRef}>
                <button
                  onClick={() => !isGenerating && setIsStageDropdownOpen(!isStageDropdownOpen)}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors border border-white/20 shadow-sm"
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${currentStageOption.color} shadow-sm`}></div>
                  <span className="text-sm font-semibold">{currentStageOption.label}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isStageDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isStageDropdownOpen && (
                  <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setIsStageDropdownOpen(false)}></div>

                    {/* Dropdown Menu */}
                    <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-gray-200 w-48 z-50 py-1">
                      {stageOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleStageChange(option.value)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2.5 group"
                        >
                          <div className={`w-2 h-2 rounded-full ${option.color} flex-shrink-0`}></div>
                          <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                            {option.label}
                          </span>
                          {currentStage === option.value && (
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Language switcher — jump between the language versions of this
                course. Purely navigation; creating/managing translations lives
                in Metadata → Translations. */}
            {hasLanguageSwitcher && (
              <div className={`relative ${isLanguageDropdownOpen ? 'z-50' : ''}`} ref={languageDropdownRef}>
                <button
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors border border-white/20"
                  title="Switch language version"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-medium">
                    {labelFor(currentLanguageCode)}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLanguageDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsLanguageDropdownOpen(false)}></div>

                    <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-gray-200 w-64 z-50 py-1">
                      <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        Language versions
                      </p>

                      {languageEntries.map((entry) => {
                        // Decided by IDENTITY -- am I on the original, or on a
                        // translation? -- never by comparing language labels.
                        const isCurrent = entry.isMaster
                          ? !isTranslatedCopy
                          : isTranslatedCopy &&
                            entry.code?.toLowerCase() === currentLanguageCode?.toLowerCase();
                        const disabled = !entry.ready;

                        return (
                          <button
                            key={entry.code}
                            onClick={() => {
                              if (isCurrent || disabled) return;
                              setIsLanguageDropdownOpen(false);
                              onSwitchLanguage?.(entry.code, entry.isMaster);
                            }}
                            disabled={disabled && !isCurrent}
                            className={`w-full px-3 py-2 text-left transition-colors flex items-center gap-2.5 group ${
                              disabled && !isCurrent
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900 line-clamp-1">
                              {labelFor(entry.code)}
                            </span>

                            {entry.isMaster && (
                              <span className="text-[10px] font-semibold text-gray-400 uppercase flex-shrink-0">
                                Original
                              </span>
                            )}
                            {!entry.ready && (
                              <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin flex-shrink-0" />
                            )}
                            {/* Words, not a bare icon: an amber triangle alone could
                                not say whether this is being fixed or just stale. */}
                            {entry.updating ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                Updating
                              </span>
                            ) : entry.ready && entry.stale ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                <AlertTriangle className="w-2.5 h-2.5" />
                                Out of date
                              </span>
                            ) : null}
                            {isCurrent && (
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}

                      {onManageTranslations && !readOnly && !isTranslatedCopy && (
                        <>
                          <div className="my-1 border-t border-gray-100"></div>
                          <button
                            onClick={() => {
                              setIsLanguageDropdownOpen(false);
                              onManageTranslations();
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2.5"
                          >
                            <Settings className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-700">
                              Manage translations
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <button
              onClick={onPreview}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors border border-white/20"
              title="Preview Course"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">Preview</span>
            </button>
            {/* Still shown when published — the metadata opens read-only, and the
                delete action inside it has its own permission. */}
            {!readOnly && (
              <button
                onClick={() => !isGenerating && onOpenMetadata()}
                disabled={isGenerating}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors shadow-sm ${
                  isGenerating
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-purple-600 hover:bg-purple-50'
                }`}
                title={isGenerating ? 'Cannot edit while course is generating' : 'Edit Metadata'}
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">Metadata</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Translated-version banner — ONE banner, saying all of it.

          There used to be two stacked here: this one, and the read-only one
          below. On a translated course both fired and said the same thing in
          different words — "manage it from the original course" directly above
          "switch to the original above to change this course" — which reads
          like the page is repeating itself because something went wrong.

          The read-only chip and the edit rule moved in here. The banner below
          now handles only the OTHER reason a course is read-only: SCORM. */}
      {isTranslatedCopy && !isGenerating && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Globe className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-indigo-900">
                {/* stripFlag: the clone copies the picker LABEL into
                    courseDetails.language, flag emoji and all. Windows has no flag
                    glyphs and renders it as the two letters of the country code, so
                    this heading read “Translated version — IN Tamil”. */}
                Translated version{courseDetails.language ? ` — ${stripFlagEmoji(courseDetails.language)}` : ''}
              </p>
              <p className="text-xs text-indigo-700 mt-0.5">
                View only — edits are made on the original and flow into every
                language. Open the original to change this course, add languages,
                or update this translation.
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              {/* The chip the teal banner used to carry. Kept because it is the
                  fastest read on the page: an admin scanning for "can I type
                  here?" finds it without reading a sentence. */}
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Read-only
              </span>
              {onOpenSourceCourse && (
                <button
                  onClick={onOpenSourceCourse}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors"
                >
                  Open original course
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Read-only banner — SCORM only.

          A translated course is also read-only, but it says so in its own
          banner above; rendering this one too gave the same course two
          near-identical notices. Saying the wrong reason is worse than saying
          nothing — an admin told "SCORM" on a Hindi course would go looking
          for a SCORM import that does not exist — so the reason is now
          attached to the banner that owns it. */}
      {readOnly && !isTranslatedCopy && !isGenerating && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-teal-900">SCORM course — view only</p>
              <p className="text-xs text-teal-700 mt-0.5">
                This course was imported from a SCORM package. Its content, modules
                and metadata can't be edited — you can preview it.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                Read-only
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Published lock banner — publishing is one-way. */}
      {isPublished && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Lock className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">Published — locked</p>
              <p className="text-xs text-green-700 mt-0.5">
                This course is live for learners. Publishing is final: its content, modules and metadata can no longer be edited, and it can't be moved back to draft.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Read-only
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Generating Warning Banner */}
      {isGenerating && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                Course is currently generating
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Editing is disabled until generation completes. You can preview the course but cannot modify content or metadata.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Read-only mode
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Section */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4 sm:p-6 bg-gray-50 rounded-b-xl">
        <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-300 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium mb-1">Modules</p>
              <p className="text-2xl font-bold text-gray-900">{courseDetails.moduleCount || 0}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <BarChart className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-indigo-300 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium mb-1">Difficulty</p>
              <p className="text-lg font-bold text-gray-900 capitalize">{courseDetails.difficulty || 'Intermediate'}</p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-lg">
              <Award className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium mb-1">Category</p>
              <p className="text-base font-bold text-gray-900 line-clamp-1">{courseDetails.category || 'General'}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-green-300 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs font-medium mb-1">Status</p>
              <p className="text-lg font-bold text-gray-900 capitalize">{courseDetails.stage || 'draft'}</p>
            </div>
            <div className={`p-3 rounded-lg ${
              courseDetails.stage === 'published'
                ? 'bg-green-100'
                : courseDetails.stage === 'draft'
                ? 'bg-yellow-100'
                : 'bg-gray-100'
            }`}>
              <Users className={`w-5 h-5 ${
                courseDetails.stage === 'published'
                  ? 'text-green-600'
                  : courseDetails.stage === 'draft'
                  ? 'text-yellow-600'
                  : 'text-gray-600'
              }`} />
            </div>
          </div>
        </div>
      </div>

      {/* Publish confirmation — publishing is irreversible. */}
      <PublishConfirmModal
        isOpen={isPublishConfirmOpen}
        onClose={() => setIsPublishConfirmOpen(false)}
        onConfirm={confirmPublish}
        isPending={isPublishing}
        entityLabel="course"
        itemName={courseDetails.title}
        lockedActions={[
          'Add, edit, delete or reorder modules and content',
          'Edit scenes or regenerate content with AI',
          'Add or remove assessments',
          'Edit the course metadata',
          'Move it back to draft',
        ]}
      />
    </div>
  );
}